/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../api/axiosInstance";
import { useSearchParams } from "react-router-dom";
import "../Reservations.css";
import Select from "react-select";
import RoomBlocks from "../Components/RoomBlocks";
import { usePmsTheme } from "../Components/PmsThemeContext";
import useCrossTabDataRefreshToken from "../hooks/useCrossTabDataRefresh";
import { DEMO_BUSINESS_UNIT_ID, isDemoMode, readDemoUser } from "../demo/demoMode";
import "./RoomsManagement.css";

const ROOM_TABLE_FIELD_KEYS = [
  "roomNumber",
  "category",
  "floor",
  "size",
  "beds",
  "bedType",
  "features",
  "view",
  "description",
];

const ROOM_FILTER_FIELD_KEYS = [
  "roomNumber",
  "category",
  "floor",
  "beds",
  "bedType",
  "features",
  "view",
  "description",
  "status",
  "size",
];

const ROOM_FIELD_VALUE_GETTERS = {
  roomNumber: (room) => room.room_number,
  category: (room) => room.RoomCategory?.name,
  floor: (room) => room.floor ?? "",
  size: (room) => room.sq_meters,
  beds: (room) => room.num_beds,
  bedType: (room) => room.bed_type,
  features: (room) => room.features,
  view: (room) => room.view,
  description: (room) => room.description,
  status: (room) => room.status,
};

const getRoomFieldValue = (room, fieldKey) => {
  const getter = ROOM_FIELD_VALUE_GETTERS[fieldKey];
  return getter ? getter(room) : "";
};

const compareNumericFilter = (roomValue, filterValue, operator = "=") => {
  const numericRoomValue = Number(roomValue);
  const numericFilterValue = Number(filterValue);

  if (!Number.isFinite(numericRoomValue) || !Number.isFinite(numericFilterValue)) {
    return false;
  }

  switch (operator) {
    case ">":
      return numericRoomValue > numericFilterValue;
    case "<":
      return numericRoomValue < numericFilterValue;
    case ">=":
      return numericRoomValue >= numericFilterValue;
    case "<=":
      return numericRoomValue <= numericFilterValue;
    default:
      return numericRoomValue === numericFilterValue;
  }
};

const getRoomsSelectStyles = (isLightTheme) => {
  if (isLightTheme) {
    return {
      control: (provided, state) => ({
        ...provided,
        minHeight: 40,
        fontSize: "0.78rem",
        backgroundColor: "#ffffff",
        borderColor: state.isFocused ? "#3db6e9" : "#7c90a7",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(61, 182, 233, 0.24)" : "none",
        color: "#26354f",
        borderRadius: 9,
        ":hover": { borderColor: "#3db6e9" },
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: "#ffffff",
        fontSize: "0.78rem",
        zIndex: 10001,
        border: "1px solid #d7e2ec",
        boxShadow: "0 18px 38px rgba(34, 58, 122, 0.16)",
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: "#edf8fc",
        borderRadius: 7,
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: "#223a7a",
        fontWeight: 700,
      }),
      multiValueRemove: (base) => ({
        ...base,
        color: "#223a7a",
        ":hover": { backgroundColor: "#fdeff1", color: "#9b3445" },
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? "#223a7a" : state.isFocused ? "#eaf7fc" : "#ffffff",
        color: state.isSelected ? "#ffffff" : "#26354f",
      }),
      input: (base) => ({ ...base, color: "#26354f" }),
      placeholder: (base) => ({ ...base, color: "#5b6680" }),
      singleValue: (base) => ({ ...base, color: "#26354f" }),
      indicatorSeparator: (base) => ({ ...base, backgroundColor: "#d7e2ec" }),
      dropdownIndicator: (base) => ({ ...base, color: "#223a7a" }),
    };
  }

  return {
    control: (provided, state) => ({
      ...provided,
      minHeight: 40,
      fontSize: "0.78rem",
      backgroundColor: "#152344",
      borderColor: state.isFocused ? "#3db6e9" : "rgba(61, 182, 233, 0.62)",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(61, 182, 233, 0.24)" : "none",
      color: "#dfe8f7",
      borderRadius: 9,
      ":hover": { borderColor: "#3db6e9" },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#0e1730",
      fontSize: "0.78rem",
      zIndex: 10001,
      border: "1px solid rgba(61, 182, 233, 0.42)",
      boxShadow: "0 20px 42px rgba(2, 8, 28, 0.5)",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#143d42",
      borderRadius: 7,
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#a9e1da",
      fontWeight: 700,
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#a9e1da",
      ":hover": { backgroundColor: "#3a1d2a", color: "#ffc7cf" },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#223a7a" : state.isFocused ? "#172a4d" : "#0e1730",
      color: "#dfe8f7",
    }),
    input: (base) => ({ ...base, color: "#dfe8f7" }),
    placeholder: (base) => ({ ...base, color: "#aebbd0" }),
    singleValue: (base) => ({ ...base, color: "#dfe8f7" }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: "rgba(61, 182, 233, 0.32)" }),
    dropdownIndicator: (base) => ({ ...base, color: "#3db6e9" }),
  };
};

const ROOMS_MANAGEMENT_MESSAGES = {
  en: {
    tabs: {
      matrix: "Room Matrix",
      blocks: "Room Blocks",
    },
    descriptions: {
      matrix: "View room availability and key details at a glance.",
      blocks: "Review and manage scheduled room blocks.",
      fallback: "Room management workspace.",
    },
    states: {
      noPermission: "You don't have permission to access Rooms Management.",
      noRooms: "No rooms found.",
    },
    buttons: {
      filters: "Filters",
      clearAll: "Clear All",
      close: "Close",
      cancel: "Cancel",
      apply: "Apply",
    },
    filter: {
      title: "Add Filter",
      mode: "Mode",
      field: "Field",
      condition: "Condition",
      value: "Value",
      values: "Values",
      single: "Single",
      multiple: "Multiple",
      selectField: "-- Select Field --",
      selectPlaceholder: "Select...",
      activeTitle: "Active Filters",
      filterBy: (label) => `Click to filter by ${label}`,
    },
    meta: {
      roomsLoaded: (roomsCount, filtersCount) =>
        `${roomsCount} rooms loaded${
          filtersCount > 0 ? ` - ${filtersCount} ${filtersCount === 1 ? "filter" : "filters"}` : ""
        }`,
    },
    fields: {
      roomNumber: "Room Number",
      category: "Category",
      floor: "Floor",
      size: "Size (m2)",
      beds: "Beds",
      bedType: "Bed Type",
      features: "Features",
      view: "View",
      description: "Description",
      status: "Status",
    },
    common: {
      notAvailable: "N/A",
    },
  },
  es: {
    tabs: {
      matrix: "Matriz de habitaciones",
      blocks: "Bloqueos de habitaciones",
    },
    descriptions: {
      matrix: "Consulta la disponibilidad y los datos clave de cada habitaci\u00f3n de un vistazo.",
      blocks: "Revisa y gestiona los bloqueos programados de habitaciones.",
      fallback: "Espacio de trabajo para la gesti\u00f3n de habitaciones.",
    },
    states: {
      noPermission: "No tienes permiso para acceder a Rooms Management.",
      noRooms: "No se han encontrado habitaciones.",
    },
    buttons: {
      filters: "Filtros",
      clearAll: "Limpiar todo",
      close: "Cerrar",
      cancel: "Cancelar",
      apply: "Aplicar",
    },
    filter: {
      title: "A\u00f1adir filtro",
      mode: "Modo",
      field: "Campo",
      condition: "Condici\u00f3n",
      value: "Valor",
      values: "Valores",
      single: "Simple",
      multiple: "M\u00faltiple",
      selectField: "-- Seleccionar campo --",
      selectPlaceholder: "Seleccionar...",
      activeTitle: "Filtros activos",
      filterBy: (label) => `Haz clic para filtrar por ${label}`,
    },
    meta: {
      roomsLoaded: (roomsCount, filtersCount) =>
        `${roomsCount} habitaciones cargadas${
          filtersCount > 0 ? ` - ${filtersCount} ${filtersCount === 1 ? "filtro" : "filtros"}` : ""
        }`,
    },
    fields: {
      roomNumber: "N\u00famero de habitaci\u00f3n",
      category: "Categor\u00eda",
      floor: "Planta",
      size: "Tama\u00f1o (m2)",
      beds: "Camas",
      bedType: "Tipo de cama",
      features: "Caracter\u00edsticas",
      view: "Vista",
      description: "Descripci\u00f3n",
      status: "Estado",
    },
    common: {
      notAvailable: "N/D",
    },
  },
  ca: {
    tabs: {
      matrix: "Matriu d'habitacions",
      blocks: "Bloquejos d'habitacions",
    },
    descriptions: {
      matrix: "Consulta la disponibilitat i les dades clau de cada habitaci\u00f3 d'una ullada.",
      blocks: "Revisa i gestiona els bloquejos programats d'habitacions.",
      fallback: "Espai de treball per a la gesti\u00f3 d'habitacions.",
    },
    states: {
      noPermission: "No tens perm\u00eds per accedir a Rooms Management.",
      noRooms: "No s'han trobat habitacions.",
    },
    buttons: {
      filters: "Filtres",
      clearAll: "Netejar-ho tot",
      close: "Tancar",
      cancel: "Cancel.lar",
      apply: "Aplicar",
    },
    filter: {
      title: "Afegir filtre",
      mode: "Mode",
      field: "Camp",
      condition: "Condici\u00f3",
      value: "Valor",
      values: "Valors",
      single: "Simple",
      multiple: "M\u00faltiple",
      selectField: "-- Seleccionar camp --",
      selectPlaceholder: "Seleccionar...",
      activeTitle: "Filtres actius",
      filterBy: (label) => `Fes clic per filtrar per ${label}`,
    },
    meta: {
      roomsLoaded: (roomsCount, filtersCount) =>
        `${roomsCount} habitacions carregades${
          filtersCount > 0 ? ` - ${filtersCount} ${filtersCount === 1 ? "filtre" : "filtres"}` : ""
        }`,
    },
    fields: {
      roomNumber: "N\u00famero d'habitaci\u00f3",
      category: "Categoria",
      floor: "Planta",
      size: "Mida (m2)",
      beds: "Llits",
      bedType: "Tipus de llit",
      features: "Caracter\u00edstiques",
      view: "Vista",
      description: "Descripci\u00f3",
      status: "Estat",
    },
    common: {
      notAvailable: "N/D",
    },
  },
};


const RoomsManagement = ({ forcedTab = null }) => {
  const { themeId, languageId } = usePmsTheme();
  const isLightTheme = themeId === "light";
  const roomsCopy = ROOMS_MANAGEMENT_MESSAGES[languageId] || ROOMS_MANAGEMENT_MESSAGES.en;
  const demoModeActive = isDemoMode();
  const currentUser = demoModeActive
    ? readDemoUser()
    : JSON.parse(localStorage.getItem("palantir_user"));
  const permissions = currentUser?.permissions || {};
  const isAdminRole = !demoModeActive && Number(currentUser?.id) === 1;
  const canViewMatrix = isAdminRole || Boolean(permissions["rooms.view_matrix"]);
  const canViewBlocks = isAdminRole || Boolean(permissions["rooms.view_blocks"]);

  const isTabAllowed = (tab) => {
    if (tab === "matrix") return canViewMatrix;
    if (tab === "blocks") return canViewBlocks;
    return false;
  };

  const firstAllowedTab = canViewMatrix ? "matrix" : canViewBlocks ? "blocks" : "matrix";

  const [activeTab, setActiveTab] = useState(
    forcedTab && isTabAllowed(forcedTab) ? forcedTab : firstAllowedTab
  );
  const [rooms, setRooms] = useState([]);
  const [filters, setFilters] = useState({});
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [selectedField, setSelectedField] = useState("");
  const [fieldOperator, setFieldOperator] = useState("=");
  const [fieldValue, setFieldValue] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);
  const [selectionMode, setSelectionMode] = useState("single");
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [blockStartDate, setBlockStartDate] = useState("");
  const [blockEndDate, setBlockEndDate] = useState("");
  const [unblockReason, setUnblockReason] = useState(""); 
  const [selectedRoomIds, setSelectedRoomIds] = useState([]);
  const [roomBlocks, setRoomBlocks] = useState([]);




  

  const [searchParams] = useSearchParams();
  const businessUnitId = demoModeActive
    ? DEMO_BUSINESS_UNIT_ID
    : searchParams.get("business_unit_id") || "1";
  const crossTabRefreshToken = useCrossTabDataRefreshToken();

  const filterBoxRef = useRef(null);
  const selectStyles = useMemo(() => getRoomsSelectStyles(isLightTheme), [isLightTheme]);
  const getFieldLabel = (fieldKey) =>
    roomsCopy.fields[fieldKey] || ROOMS_MANAGEMENT_MESSAGES.en.fields[fieldKey] || fieldKey;

  const closeFilterPopup = () => {
    setShowFilterPopup(false);
    setSelectedField("");
    setFieldValue("");
  };

  useEffect(() => {
    if (forcedTab) {
      if (isTabAllowed(forcedTab)) {
        setActiveTab(forcedTab);
      } else {
        setActiveTab(firstAllowedTab);
      }
      return;
    }

    const tabFromUrl = searchParams.get("tab");
    if (!tabFromUrl) {
      setActiveTab(firstAllowedTab);
      return;
    }

    if (isTabAllowed(tabFromUrl)) {
      console.log("[RoomsManagement][Tab] set from URL", { tabFromUrl });
      setActiveTab(tabFromUrl);
    } else {
      console.warn("[RoomsManagement][Tab] invalid tab in URL, ignoring", { tabFromUrl });
      setActiveTab(firstAllowedTab);
    }
  }, [firstAllowedTab, forcedTab, searchParams]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/rooms?business_unit_id=${businessUnitId}`)
      .then(response => {
        const sortedRooms = response.data.sort((a, b) => {
          if (a.RoomCategory?.name > b.RoomCategory?.name) return 1;
          if (a.RoomCategory?.name < b.RoomCategory?.name) return -1;
          return a.room_number - b.room_number;
        });
        setRooms(sortedRooms);
        console.log("[Rooms][UI] Loaded rooms:", sortedRooms);
      })
      .catch(error => console.error("Error fetching rooms:", error));
  }, [businessUnitId, crossTabRefreshToken]);

  useEffect(() => {
    if (!showFilterPopup) return undefined;

    const previousFocus = document.activeElement;
    const dialog = filterBoxRef.current;
    const focusableSelector =
      'button:not(:disabled), select:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex="-1"])';
    const focusable = dialog ? Array.from(dialog.querySelectorAll(focusableSelector)) : [];
    focusable[0]?.focus();

    const handleDialogKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeFilterPopup();
        return;
      }

      if (event.key !== "Tab" || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleDialogKeyDown);
    return () => {
      document.removeEventListener("keydown", handleDialogKeyDown);
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, [showFilterPopup]);

  const handleConfirmBlock = async () => {
    if (demoModeActive) return;
    if (selectedRoomIds.length === 0) {
      alert("Please select at least one room");
      return;
    }

    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/room-blocks`,
      {
        room_ids: selectedRoomIds,
        start_date: blockStartDate,
        end_date: blockEndDate,
        reason: blockReason,
      }
    );


    console.log("[Rooms][UI] Blocked rooms:", selectedRoomIds);

    setShowBlockModal(false);
    setBlockReason("");
    setBlockStartDate("");
    setBlockEndDate("");
    setSelectedRoomIds([]);
  };

  useEffect(() => {
    if (!demoModeActive && showUnblockModal) {
      axios.get(`${process.env.REACT_APP_API_URL}/api/room-blocks`)
        .then(res => setRoomBlocks(res.data))
        .catch(err => console.error("[Rooms][UI] Error fetching room blocks:", err));
    }
  }, [demoModeActive, showUnblockModal]);




  const handleHeaderClick = (field) => {
    setSelectedField(field);
    setFieldValue("");
    setShowFilterPopup(true);
  };


  const getFilteredRooms = (excludeField = null) => {
    if (activeFilters.length === 0) return rooms;

    const filtersByField = activeFilters.reduce((acc, f) => {
      if (f.field === excludeField) return acc;
      if (!acc[f.field]) acc[f.field] = [];
      acc[f.field].push(f);
      return acc;
    }, {});

    return rooms.filter(room => {
      return Object.entries(filtersByField).every(([field, fieldFilters]) => {
        const roomValue = getRoomFieldValue(room, field);
        if (field === "size") {
          return fieldFilters.every((filter) => compareNumericFilter(roomValue, filter.value, filter.operator));
        }

        const stringified = String(roomValue ?? "").toLowerCase();
        const matches = fieldFilters.some((filter) => stringified === String(filter.value ?? "").toLowerCase());

        console.log(`[Rooms][Filter] Field: "${field}"`);
        console.log(`   [Rooms][Filter] Room value: "${roomValue}" -> normalized: "${stringified}"`);
        console.log(`   [Rooms][Filter] Filter values:`, fieldFilters.map((filter) => filter.value));
        console.log(`   ✅ Match: ${matches}`);

        return matches;
      });
    });
  };

  const filteredRooms = getFilteredRooms();
  const filterOptions =
    selectedField && selectedField !== "size"
      ? [...new Set(
          getFilteredRooms(selectedField)
            .map((room) => getRoomFieldValue(room, selectedField))
            .filter((value) => value !== null && value !== undefined && value !== "")
        )]
          .sort((a, b) =>
            String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" })
          )
          .map((value) => ({
            value: String(value),
            label: String(value),
          }))
      : [];

  if (!canViewMatrix && !canViewBlocks) {
    return (
      <div className={`rooms-access-message${isLightTheme ? " rooms-access-message--light" : ""}`}>
        {roomsCopy.states.noPermission}
      </div>
    );
  }


  return (
    <div className={`reservations-container rooms-page${isLightTheme ? " rooms-page--light" : ""}`}>
      {!forcedTab && (
        <div className="rooms-toolbar">
          <div>
            <div className="rooms-tablist" role="tablist">
              {canViewMatrix && (
                <button
                  type="button"
                  className={`rooms-tabBtn ${activeTab === "matrix" ? "rooms-tabBtn--active" : ""}`}
                  onClick={() => setActiveTab("matrix")}
                  role="tab"
                >
                  {roomsCopy.tabs.matrix}
                </button>
              )}
              {canViewBlocks && (
                <button
                  type="button"
                  className={`rooms-tabBtn ${activeTab === "blocks" ? "rooms-tabBtn--active" : ""}`}
                  onClick={() => setActiveTab("blocks")}
                  role="tab"
                >
                  {roomsCopy.tabs.blocks}
                </button>
              )}
            </div>
          </div>
          <div className="rooms-toolbar-actions">
            {activeTab === "matrix" && (
              <button type="button" className="rooms-filter-btn" onClick={() => setShowFilterPopup(true)}>
                {roomsCopy.buttons.filters}
              </button>
            )}
          </div>
        </div>
      )}

      {activeTab === "matrix" && activeFilters.length > 0 && (
        <div className="rooms-active-filters">
          <h4>{roomsCopy.filter.activeTitle}</h4>
          {activeFilters.map((f, i) => (
            <div key={i} className="rooms-active-filter-pill">
              <span>
                {getFieldLabel(f.field)}: <strong>{f.value}</strong>
              </span>
              <button
                type="button"
                className="rooms-active-filter-remove"
                title={roomsCopy.buttons.close}
                aria-label={`${roomsCopy.buttons.close}: ${getFieldLabel(f.field)} ${f.value}`}
                onClick={() => {
                  setActiveFilters(prev => prev.filter((_, idx) => idx !== i));
                }}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className="rooms-active-filter-clear"
            onClick={() => {
              setActiveFilters([]);
              setSelectedField("");
              setFieldValue("");
            }}
          >
            {roomsCopy.buttons.clearAll}
          </button>
        </div>
      )}

      <div className="rooms-panel">
        {activeTab === "matrix" && (
          <div className="rooms-table-card">
            <div className="rooms-table-header">
              <div>
                <h3>{roomsCopy.tabs.matrix}</h3>
                <p className="rooms-table-meta">
                  {roomsCopy.meta.roomsLoaded(rooms.length, activeFilters.length)}
                </p>
              </div>
              {forcedTab && (
                <button type="button" className="rooms-filter-btn" onClick={() => setShowFilterPopup(true)}>
                  {roomsCopy.buttons.filters}
                </button>
              )}
            </div>
            <div className="rooms-table-scroll">
              <table className="rooms-table" style={{ tableLayout: "fixed", width: "100%" }}>
                <colgroup>
                  <col style={{ width: "110px" }} />
                  <col style={{ width: "180px" }} />
                  <col style={{ width: "60px" }} />
                  <col style={{ width: "60px" }} />
                  <col style={{ width: "60px" }} />
                  <col style={{ width: "170px" }} />
                  <col style={{ width: "180px" }} />
                  <col style={{ width: "120px" }} />
                  <col style={{ width: "260px" }} />
                </colgroup>

                <thead>
                  <tr>
                    {ROOM_TABLE_FIELD_KEYS.map((fieldKey, index) => (
                      <th key={index}>
                        <button
                          type="button"
                          className="rooms-column-filter"
                          onClick={() => handleHeaderClick(fieldKey)}
                          title={roomsCopy.filter.filterBy(getFieldLabel(fieldKey))}
                          aria-label={roomsCopy.filter.filterBy(getFieldLabel(fieldKey))}
                        >
                          {getFieldLabel(fieldKey)}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {filteredRooms.length > 0 ? (
                    filteredRooms.map((room) => (
                      <tr key={room.id}>
                        <td>{room.room_number}</td>
                        <td>{room.RoomCategory?.name || roomsCopy.common.notAvailable}</td>
                        <td>{room.floor}</td>
                        <td>{room.sq_meters}</td>
                        <td>{room.num_beds}</td>
                        <td
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={room.bed_type || roomsCopy.common.notAvailable}
                        >
                          {room.bed_type || roomsCopy.common.notAvailable}
                        </td>
                        <td
                          style={{
                            maxWidth: "150px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={room.features || roomsCopy.common.notAvailable}
                        >
                          {room.features || roomsCopy.common.notAvailable}
                        </td>
                        <td
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={room.view || roomsCopy.common.notAvailable}
                        >
                          {room.view || roomsCopy.common.notAvailable}
                        </td>
                        <td
                          style={{
                            maxWidth: "150px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={room.description || roomsCopy.common.notAvailable}
                        >
                          {room.description || roomsCopy.common.notAvailable}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-3">
                        {roomsCopy.states.noRooms}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {activeTab === "blocks" && canViewBlocks && (
          <RoomBlocks />
        )}
      </div>

      {activeTab === "matrix" && showFilterPopup && (
        <div className="rooms-filter-overlay">
          <div
            ref={filterBoxRef}
            className="rooms-filter-box"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rooms-filter-title"
            tabIndex={-1}
          >
            <div className="rooms-filter-header">
              <span id="rooms-filter-title" className="rooms-filter-title">{roomsCopy.filter.title}</span>
              <button
                type="button"
                className="rooms-filter-close"
                onClick={closeFilterPopup}
              >
                {roomsCopy.buttons.close}
              </button>
            </div>

            <div className="rooms-filter-fields">
              <div>
                <label className="form-label mb-1">{roomsCopy.filter.mode}</label>
                <select
                  className="rooms-filter-select"
                  value={selectionMode}
                  onChange={(e) => setSelectionMode(e.target.value)}
                >
                  <option value="single">{roomsCopy.filter.single}</option>
                  <option value="multiple">{roomsCopy.filter.multiple}</option>
                </select>
              </div>

              <div>
                <label className="form-label mb-1">{roomsCopy.filter.field}</label>
                <select
                  className="rooms-filter-select"
                  value={selectedField}
                  onChange={(e) => {
                    setSelectedField(e.target.value);
                    setFieldValue("");
                  }}
                >
                  <option value="">{roomsCopy.filter.selectField}</option>
                  {ROOM_FILTER_FIELD_KEYS.map((fieldKey, i) => (
                    <option key={i} value={fieldKey}>
                      {getFieldLabel(fieldKey)}
                    </option>
                  ))}
                </select>
              </div>

              {selectedField && selectedField === "size" && (
                <>
                  <div>
                    <label className="form-label">{roomsCopy.filter.condition}</label>
                    <select
                      className="rooms-filter-select"
                      value={fieldOperator}
                      onChange={(e) => setFieldOperator(e.target.value)}
                    >
                    <option value="=">=</option>
                      <option value=">">&gt;</option>
                      <option value="<">&lt;</option>
                      <option value=">=">&gt;=</option>
                      <option value="<=">&lt;=</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">{roomsCopy.filter.value}</label>
                    <input
                      className="rooms-filter-input"
                      type="number"
                      value={fieldValue}
                      onChange={(e) => setFieldValue(e.target.value)}
                    />
                  </div>

                  <div className="rooms-filter-actions">
                    <button
                      type="button"
                      className="rooms-filter-action rooms-filter-action--secondary"
                      onClick={closeFilterPopup}
                    >
                      {roomsCopy.buttons.cancel}
                    </button>
                    <button
                      type="button"
                      className="rooms-filter-action rooms-filter-action--primary"
                      onClick={() => {
                        if (!fieldValue) return;
                        setActiveFilters((prev) => [
                          ...prev.filter((f) => f.field !== selectedField),
                          { field: selectedField, value: fieldValue, operator: fieldOperator },
                        ]);
                        closeFilterPopup();
                      }}
                      disabled={!fieldValue}
                    >
                      {roomsCopy.buttons.apply}
                    </button>
                  </div>
                </>
              )}

              {selectedField && selectedField !== "size" && (
                <div>
                  <label className="form-label mb-1">
                    {selectionMode === "multiple" ? roomsCopy.filter.values : roomsCopy.filter.value}
                  </label>
                  <Select
                    isMulti={selectionMode === "multiple"}
                    placeholder={roomsCopy.filter.selectPlaceholder}
                    value={activeFilters
                      .filter((f) => f.field === selectedField)
                      .map((f) => ({ value: f.value, label: f.value }))}
                    onChange={(selectedOptions) => {
                      const values = Array.isArray(selectedOptions)
                        ? selectedOptions.map((opt) => opt.value)
                        : selectedOptions?.value
                          ? [selectedOptions.value]
                          : [];

                      setActiveFilters((prev) => [
                        ...prev.filter((f) => f.field !== selectedField),
                        ...values.map((val) => ({ field: selectedField, value: val })),
                      ]);

                      if (selectionMode === "single") {
                        setTimeout(() => {
                          setSelectedField("");
                          setFieldValue("");
                          setShowFilterPopup(false);
                        }, 0);
                      }
                    }}
                    options={filterOptions}
                    styles={selectStyles}
                  />
                  <div className="rooms-filter-actions">
                    <button
                      type="button"
                      className="rooms-filter-action rooms-filter-action--secondary"
                      onClick={closeFilterPopup}
                    >
                      {roomsCopy.buttons.close}
                    </button>
                  </div>
                </div>
              )}

              {!selectedField && (
                <div className="rooms-filter-actions">
                  <button
                    type="button"
                    className="rooms-filter-action rooms-filter-action--secondary"
                    onClick={closeFilterPopup}
                  >
                    {roomsCopy.buttons.close}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomsManagement;
